import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileToCompressedDataURL } from "@/lib/image";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_OPTIONS = ["CMA USA", "ACCA", "CA", "CS", "CFA"];

const emptyTopper = {
  name: "",
  course: "CMA USA",
  part: "Part 1+2",
  mark: 0,
  max_mark: 1000,
  scores: [],
  photo: "",
  featured: true,
  order: 0,
};

export default function TopperEditDialog({ open, onOpenChange, topper, authHeader, onSaved }) {
  const isEdit = !!topper?.id;
  const [form, setForm] = useState(emptyTopper);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(topper ? { ...emptyTopper, ...topper, scores: topper.scores || [] } : emptyTopper);
    }
  }, [open, topper]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataURL(file, { maxDim: 800, quality: 0.85 });
      update("photo", dataUrl);
      toast.success("Photo ready. Click Save to persist.");
    } catch (err) {
      toast.error("Could not read the image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const isACCA = form.course === "ACCA";

  const addScore = () => update("scores", [...(form.scores || []), { paper: "", mark: 50 }]);
  const updateScore = (i, key, val) => {
    const copy = [...form.scores];
    copy[i] = { ...copy[i], [key]: key === "mark" ? Number(val) : val };
    update("scores", copy);
  };
  const removeScore = (i) => update("scores", form.scores.filter((_, j) => j !== i));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        course: form.course,
        part: form.part || null,
        mark: Number(form.mark) || 0,
        max_mark: Number(form.max_mark) || 1000,
        scores: (form.scores || []).filter((s) => s.paper && s.paper.trim()),
        photo: form.photo || "",
        featured: !!form.featured,
        order: Number(form.order) || 0,
      };
      if (isEdit) {
        await axios.put(`${API}/toppers/${topper.id}`, payload, { headers: authHeader });
        toast.success("Topper updated");
      } else {
        await axios.post(`${API}/toppers`, payload, { headers: authHeader });
        toast.success("Topper added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto rounded-3xl p-0 border-0 shadow-2xl" data-testid="topper-edit-dialog">
        <div className="bg-[#002570] text-white p-6 md:p-7">
          <DialogHeader>
            <DialogTitle className="font-rubik text-xl md:text-2xl font-bold">
              {isEdit ? "Edit Topper" : "Add New Topper"}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-white/80 mt-1">
              Photos auto-resize to 800px to keep the database lean.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={save} className="p-6 md:p-7 space-y-5 bg-white" data-testid="topper-edit-form">
          {/* Photo */}
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              {form.photo ? (
                <img src={form.photo} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="font-rubik text-xl font-bold text-slate-400">
                  {form.name ? form.name.split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") : "—"}
                </span>
              )}
            </div>
            <div className="flex-1">
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Photo</label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-4 py-2 text-sm font-medium cursor-pointer transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {form.photo ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onPickPhoto}
                    data-testid="topper-photo-input"
                    className="hidden"
                  />
                </label>
                {form.photo && (
                  <button type="button" onClick={() => update("photo", "")} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-3 py-2">
                    <X className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Name + Course */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</label>
              <input
                required
                data-testid="topper-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Krishna Kanth"
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Course *</label>
              <select
                data-testid="topper-course-select"
                value={form.course}
                onChange={(e) => update("course", e.target.value)}
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              >
                {COURSE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {!isACCA ? (
            <>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Part</label>
                  <input
                    data-testid="topper-part-input"
                    value={form.part || ""}
                    onChange={(e) => update("part", e.target.value)}
                    placeholder="Part 1+2"
                    className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                  />
                </div>
                <div>
                  <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Mark</label>
                  <input
                    type="number"
                    data-testid="topper-mark-input"
                    value={form.mark}
                    onChange={(e) => update("mark", e.target.value)}
                    className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                  />
                </div>
                <div>
                  <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Max</label>
                  <input
                    type="number"
                    data-testid="topper-max-input"
                    value={form.max_mark}
                    onChange={(e) => update("max_mark", e.target.value)}
                    className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                  />
                </div>
              </div>
            </>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">ACCA Papers Cleared</label>
                <button type="button" onClick={addScore} data-testid="topper-add-score-btn" className="inline-flex items-center gap-1 text-sm text-[#002570] hover:text-[#001a52] font-medium">
                  <Plus className="w-4 h-4" /> Add paper
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {(form.scores || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      placeholder="Paper code (e.g. SBL)"
                      value={s.paper}
                      onChange={(e) => updateScore(i, "paper", e.target.value)}
                      className="flex-1 bg-white rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                    />
                    <input
                      type="number"
                      placeholder="Mark"
                      value={s.mark}
                      onChange={(e) => updateScore(i, "mark", e.target.value)}
                      className="w-24 bg-white rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                    />
                    <button type="button" onClick={() => removeScore(i)} className="w-9 h-9 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(!form.scores || form.scores.length === 0) && (
                  <p className="text-xs text-slate-500 italic">Click "Add paper" to record an ACCA paper score.</p>
                )}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Display Order</label>
              <input
                type="number"
                data-testid="topper-order-input"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
                className="mt-1.5 w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
              />
              <p className="mt-1 text-xs text-slate-500">Lower numbers appear first.</p>
            </div>
            <label className="flex items-center gap-3 mt-7 select-none cursor-pointer">
              <input
                type="checkbox"
                data-testid="topper-featured-checkbox"
                checked={form.featured}
                onChange={(e) => update("featured", e.target.checked)}
                className="w-5 h-5 rounded text-[#002570] focus:ring-[#73c3ff]"
              />
              <span className="font-inter text-sm font-medium text-slate-700">Show on public Toppers page</span>
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-full">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="topper-save-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3 font-inter font-medium transition-all"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : isEdit ? "Save Changes" : "Add Topper"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
